// reportsApi — wraps the existing /v1/reports endpoint (report-api).
// Backend is already real; this PR only wires the SPA.
import { apiFetch } from "../../lib/api-client.js";

export type ReportKind = "daily" | "weekly" | "monthly" | "custom";

export interface ReportSummary {
  report_id: string;
  title: string;
  scope_label: string;
  kind: ReportKind;
  date_local: string;
  generated_at_local: string;
  status: "ready" | "queued" | "failed";
}

export interface ReportListResponse {
  tenant_id: string;
  kind: ReportKind;
  reports: ReportSummary[];
  notice?: string;
  correlation_id: string | null;
}

export interface ReportDetailResponse {
  report_id: string;
  tenant_id: string;
  kind: string;
  date_local: string;
  generated_at_local: string;
  status: "ready" | "queued" | "failed";
  size_kb: number | null;
  download_url: string | null;
  summary?: Record<string, unknown>;
  preview_sections?: Array<{ title: string; lines: string[] }>;
  correlation_id: string | null;
}

export class ReportsApiError extends Error {
  status: number;
  code: string;
  correlationId: string | null;
  constructor(status: number, code: string, correlationId: string | null) {
    super(code);
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }
}

export function friendlyReportsError(err: unknown): string {
  if (err instanceof ReportsApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色看不到這份報表。";
    if (err.status === 404) return "找不到這份報表。";
    if (err.status === 400 && err.code === "tenant_id_required")
      return "尚未選定操作的單位，請先到「平台 / 租戶」選擇要進入的單位。";
    return "報表暫時無法載入。";
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<ReportsApiError> {
  let code = "request_failed";
  let cid: string | null = r.headers.get("x-correlation-id");
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string };
    code = body.error ?? code;
    cid = body.correlation_id ?? cid;
  } catch {}
  return new ReportsApiError(r.status, code, cid);
}

export async function listReports(kind: ReportKind = "daily", tenantId?: string | null): Promise<ReportListResponse> {
  const qs = new URLSearchParams({ kind });
  if (tenantId) qs.set("tenant_id", tenantId);
  const r = await apiFetch(`/v1/reports?${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as ReportListResponse;
}

export async function getReport(reportId: string, tenantId?: string | null): Promise<ReportDetailResponse> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/reports/${encodeURIComponent(reportId)}${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as ReportDetailResponse;
}
